import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import { CommonErrorCode, UserErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';
import { L10n } from '~src/commonInfra/L10n/L10n';

import { Formatters,Messages } from '../../../codegen/Localize';
import { createMultipleItemResultNotificationValues } from '../../../commonInfra/contentActions/ActionMessages';
import { ContentActionNotification} from '../../../commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem,
  MultipleItemResult
} from '../../../commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '../../../commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { ListResult } from '../../../httpClient/fetchers/ListResult';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { SiteRolesResource } from '../../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../../userManagement/services/resources/SiteUsers';
import { ISiteUser } from '../../../userManagement/users/serverApi/types/ISiteUser';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import {
  UpdateUserAuthenticationDialogBody,
  UpdateUserAuthenticationDialogProps,
  UpdateUserAuthenticationDialogState,
  UpdateUserAuthenticationWarningDialog
} from '../components/UpdateUserAuthenticationDialog';
import { getDefaultAuthenticationProviderSelectorProperties } from '../model/AuthenticationProviderSelectorProperties';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  is3rdParty,
  SiteAuthenticationSettingsService
} from './SiteAuthenticationSettingsService';

export namespace UpdateUserAuthenticationAction {

  export async function showUpdateUserAuthenticationDialog(siteUsersToUpdate: Set<ISiteUser>): Promise<any> {
    const getProviderDescription = (provider: IAuthenticationProvider) => {
      const translateValues = {
        SERVER_NAME: ServerSettingsUnauthenticated.serverName(),
        PROVIDER_NAME: SiteAuthenticationSettingsService.getProviderDisplayName(provider),
      };

      switch(provider.type){
        case AuthenticationProviderType.default:
          return Messages.setUsersAuthentication_description_internal(translateValues);
        case AuthenticationProviderType.tabidmfa:
          return Messages.setUsersAuthentication_description_tableauid_mfa(translateValues);
        default:
          if (provider.idpConfigurationId != null) {
            return;
          }
          return Messages.setUsersAuthentication_description_external(translateValues);
      }
    };

    const authenticationProviderSelectorProperties = await getAuthenticationProvider(siteUsersToUpdate, getProviderDescription);

    const dialogInstance = openReactActionDialog<UpdateUserAuthenticationDialogProps, UpdateUserAuthenticationDialogState, any>({
      title: Messages.updateUserAuthenticationTitle(),
      bodyComponent: UpdateUserAuthenticationDialogBody,
      initialBodyProps: {
        currentUser: siteUsersToUpdate.get(getSessionManagementServiceInstance().sessionUserInfo().id),
        isAboutToBeLoggedOut: false,
        isAdminToBeUpdated: determineIfAdminRolesAreToBeUpdated(siteUsersToUpdate)
      },
      initialDialogState: {...authenticationProviderSelectorProperties},
      actionName: Messages.update(),
      action: (bodyProps: UpdateUserAuthenticationDialogProps, dialogState: UpdateUserAuthenticationDialogState) => {
        const provider = dialogState.authenticationProvider;
        const userIds = siteUsersToUpdate.map(user => user.id);
        const idpConfigurationName: string = provider.idpConfigurationName;
        const idpConfigurationId: string = provider.idpConfigurationId;
        const updateUserAuthentication = () => {
          const promise = SiteAuthenticationSettingsService.setProviderIdForUsers(provider.id, userIds, idpConfigurationName, idpConfigurationId);
          showNotification(siteUsersToUpdate, promise);
          return promise;
        };

        return determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate, provider).then((showWarning: boolean) => {
          if (showWarning) {
            const warningDialog = openReactConfirmationDialog<void, any>({
              title: Messages.updateUserAuthenticationTitle(),
              message: '',
              actionName: Messages.update(),
              action: updateUserAuthentication,
              paramsForAction: undefined,
              isActionDelete: true,
              bodyComponent: UpdateUserAuthenticationWarningDialog,
              dialogWidth: TabDialogWidth.width375px
            });
            return Promise.resolve(warningDialog.result);
          } else {
            return updateUserAuthentication();
          }
        });
      },
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: false
    });

    return Promise.resolve(dialogInstance.result);
  }

  async function getAuthenticationProvider(siteUsersToUpdate: Set<ISiteUser>, getProviderDescription: (provider: IAuthenticationProvider) => string) {
    const settings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings();
    const authenticationProviderIds = Set.fromKeyArray(siteUsersToUpdate.map(u => u.authenticationProviderId));
    const authenticationProvider = authenticationProviderIds.size() === 1
      ? FeatureFlags.featureFlagForMultipleIDPSupportEnabled? SiteAuthenticationSettingsService.findProviderForMultipleIdp(settings, authenticationProviderIds.first(), siteUsersToUpdate.first().idpConfigurationId) :
      SiteAuthenticationSettingsService.findProvider(settings, authenticationProviderIds.first())
      : undefined;
    const defaultAuthenticationProviderSelectorProperties = getDefaultAuthenticationProviderSelectorProperties();

    return {
      ...defaultAuthenticationProviderSelectorProperties,
      authenticationProviderIds: authenticationProviderIds.toArray(),
      authenticationProvider: authenticationProvider,
      oldAuthenticationProvider: authenticationProvider,
      getProviderDescription: getProviderDescription};
  }

  function determineIfAdminRolesAreToBeUpdated(siteUsersToUpdate: Set<ISiteUser>): boolean {
    return siteUsersToUpdate.map(user => user.siteRole).some(role => SiteRolesResource.instance.getAdminRoles().includes(role));
  }

  function showNotification(siteUsersToUpdate: Set<ISiteUser>, promise: Promise<IResult>) {
    promise.catch(() => {
      ContentActionNotification.displayFailureToast(Messages.unexpectedError());
    });

    promise.then((result: IResult) => {
      const multipleItemResult = new MultipleItemResult(siteUsersToUpdate, result);
      ContentActionNotification.displayItemizedToastsForResult(createMultipleItemResultNotificationValues(multipleItemResult,
        {
          describeSuccessSummary: (successCount: number) =>
            Messages.updateUserAuthenticationSuccessSummary({COUNT: successCount, COUNT_LOC: Formatters.formatCount(successCount)}),

          describeErrorSummary: (errorCount: number) =>
            Messages.updateUserAuthenticationErrorSummary({COUNT: errorCount, COUNT_LOC: Formatters.formatCount(errorCount)}),

          describeErrorDetail: (errorItem: IErrorItem<ISiteUser>) => {
            return {
              itemName: errorItem.item && errorItem.item.displayName,
              itemUrl: null,
              errorMessage: getErrorDetailText(errorItem.error, errorItem.item)
            };
          }
        }));
    });
  }

  function getErrorDetailText(error: IError, user: ISiteUser) {
    switch(error.code) {
      case CommonErrorCode.SiteSAMLUserExistsOnAnotherSite:
        return Messages.userNameExistsOnServerForUpdateAuthenticationToSiteSAML();
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return Messages.systemAdminNotAllowedAsSiteSAMLUser();
      case CommonErrorCode.GuestUserCannotBeSiteSAMLUser:
        return Messages.guestUserNotAllowedAsSiteSAMLUser();
      case UserErrorCode.UserInvalidAuthenticationSetting:
        return Messages.errorUserCannotHaveTableauAuthType({ USER_NAME: L10n.elideIfLong(user.username) });
      default:
        return Messages.unexpectedError();
    }
  }

  function determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate: Set<ISiteUser>, newAuthenticationProvider: IAuthenticationProvider): Promise<boolean> {
    const defaultAuthenticationProviderId = SiteAuthenticationSettingsService.getDefaultProvider().id;
    const authenticatedByDefaultProvider = (user: ISiteUser) => user.authenticationProviderId === defaultAuthenticationProviderId;

    if (!is3rdParty(newAuthenticationProvider.id)) {
      return Promise.resolve(false);
    }

    return SiteUsers.instance.getUsersBySiteRoles(SiteRolesResource.instance.getAdminRoles()).then((result: ListResult<ISiteUser>) => {
      let adminsUsingDefaultProviderBefore = Set.fromIdItemArray(result.items).filter(authenticatedByDefaultProvider);
      let adminsUsingDefaultProviderAfter = adminsUsingDefaultProviderBefore.clone();
      adminsUsingDefaultProviderAfter.removeItemsIn(siteUsersToUpdate);
      return !adminsUsingDefaultProviderBefore.isEmpty() && adminsUsingDefaultProviderAfter.isEmpty();
    });
  }
}

export type UpdateUserAuthenticationAction = typeof UpdateUserAuthenticationAction;
